/**
 * 📱 MOBILE TEXT UTILITIES
 * Functions for text processing and truncation on mobile interface
 */

/**
 * Truncate content to specified number of words (mobile interface only)
 * @param text - Text content to truncate
 * @param maxWords - Maximum number of words (default: 300)
 * @param addEllipsis - Whether to add "..." at the end (default: true)
 * @returns Truncated text
 */
export const truncateToWords = (
  text: string, 
  maxWords: number = 300, 
  addEllipsis: boolean = true
): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Split by spaces to get words (cụm ký tự liền nhau nằm giữa 2 dấu space)
  const words = text.trim().split(/\s+/).filter(word => word.length > 0);
  
  // If already within limit, return as-is
  if (words.length <= maxWords) {
    return text;
  }
  
  // Truncate to maxWords
  const truncatedWords = words.slice(0, maxWords);
  let truncatedText = truncatedWords.join(' ');
  
  // Add ellipsis if truncated and requested
  if (addEllipsis && words.length > maxWords) {
    truncatedText += '...';
  }
  
  return truncatedText;
};

/**
 * Count words in text (for debugging/analytics)
 * @param text - Text to count words in
 * @returns Number of words
 */
export const countWords = (text: string): number => {
  if (!text || typeof text !== 'string') {
    return 0;
  }
  
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Check if text needs truncation
 * @param text - Text to check
 * @param maxWords - Maximum word limit
 * @returns Boolean indicating if truncation is needed
 */
export const needsTruncation = (text: string, maxWords: number = 300): boolean => {
  return countWords(text) > maxWords;
};

/**
 * Get truncation info for debugging
 * @param text - Original text
 * @param maxWords - Word limit
 * @returns Object with truncation statistics
 */
export const getTruncationInfo = (text: string, maxWords: number = 300) => {
  const originalWords = countWords(text);
  const needsTrunc = needsTruncation(text, maxWords);
  const truncatedText = truncateToWords(text, maxWords);
  const finalWords = countWords(truncatedText);
  
  return {
    originalWords,
    finalWords,
    needsTruncation: needsTrunc,
    wordsRemoved: originalWords - finalWords,
    reductionPercent: originalWords > 0 ? ((originalWords - finalWords) / originalWords * 100).toFixed(1) : '0'
  };
};

/**
 * Format text with line breaks following new PC formatting rules
 * @param text - Text to format
 * @returns Formatted text with line breaks
 */
export const formatSentencesWithLineBreaks = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Vietnamese sentence detection - matches sentences ending with ., !, or ?
  const sentenceRegex = /([^.!?]*[.!?]+)/g;
  const sentences = text.match(sentenceRegex);
  
  if (!sentences || sentences.length <= 2) {
    return text; // Return original if 2 or fewer sentences
  }

  let formattedText = '';
  
  for (let i = 0; i < sentences.length; i++) {
    formattedText += sentences[i].trim();
    
    if (i < sentences.length - 1) {
      // After every 4 sentences (4 dots): paragraph break (2 line breaks)
      if ((i + 1) % 4 === 0) {
        formattedText += '\n\n';
      }
      // After every 2 sentences (2 dots): single line break  
      else if ((i + 1) % 2 === 0) {
        formattedText += '\n';
      }
      // Between sentences within same group: space
      else {
        formattedText += ' ';
      }
    }
  }
  
  return formattedText;
};

/**
 * Process content for display (format sentences + optional truncation)
 * @param text - Original text
 * @param isMobile - Whether this is for mobile display
 * @param maxWords - Maximum words for mobile (default: 200)
 * @param forHTML - Whether to convert line breaks to HTML (default: auto-detect based on platform)
 * @returns Processed text with formatting and optional truncation
 */
export const processContentForDisplay = (
  text: string, 
  isMobile: boolean = false, 
  maxWords: number = 200,
  forHTML?: boolean
): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Step 1: Format sentences with line breaks (for both desktop and mobile)
  const formattedText = formatSentencesWithLineBreaks(text);
  
  // Step 2: Truncate if mobile and content is long
  const finalText = isMobile ? truncateToWords(formattedText, maxWords, true) : formattedText;
  
  // Step 3: Convert line breaks to HTML ONLY for PC (dangerouslySetInnerHTML)
  // Mobile uses plain text rendering, so keep \n as-is
  const shouldConvertToHTML = forHTML !== undefined ? forHTML : !isMobile;
  
  if (shouldConvertToHTML) {
    // PC: Convert to HTML for dangerouslySetInnerHTML
    const htmlText = finalText
      .replace(/\n\n/g, '<br><br>')  // Double breaks first
      .replace(/\n/g, '<br>');       // Then single breaks
    return htmlText;
  } else {
    // Mobile: Keep plain text with \n (works fine in text context)
    return finalText;
  }
}; 